import React from 'react';
import {
  Cell,
  Header4,
  List,
  LoadingAnimation,
  Text,
  TextField,
  useShowContextRequired
} from 'ui-builder';
import { AccessChecker } from 'authorization-scope';
import {
  ConnectionProfile,
  FUB_PIPELINES_MAPPING_UPDATED,
  FUB_STATUS_MAP_UPDATED,
  ProjectStatus,
  ProjectType,
  TeamPermission, useFubPipelineMappingService,
  useFubStagesService,
  useFubStatusMapService,
  useProjectStatusHelper
} from 'nekst-api';
import { GridLayout, IconType, OpenFormIconButton } from 'features/nekst-widgets';
import { UpdateStageToStatusEntryForm } from './UpdateStageToStatusEntryForm';
import { useDataLoaderNew } from 'data-loader';

type ListItem = {
  status: ProjectStatus,
  stageId?: number,
  stageName?: string
}

function ProjectStatusField() {
  const showContext = useShowContextRequired<ListItem>();

  const projectStatusHelper = useProjectStatusHelper();

  return (
    <Text>
      {projectStatusHelper.getLabel(showContext.data.status)}
    </Text>
  );
}

function EditButton(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
}) {

  const showContext = useShowContextRequired<ListItem>();

  return (
    <OpenFormIconButton
      form={(
        <UpdateStageToStatusEntryForm
          projectType={props.projectType}
          projectStatus={showContext.data.status}
          profile={props.profile}
        />
      )}
      type={IconType.PENCIL}
    />
  );
}

function PipelineSetView(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
}) {
  const fubStagesService = useFubStagesService();
  const fubStatusMapService = useFubStatusMapService();

  async function loadStages() {
    const map = await fubStatusMapService.getMap(props.profile.id, props.projectType);

    const stages = await fubStagesService.getAllStages(props.profile.id, props.projectType);
    const stagesMap = stages.reduce((acc, stage) => {
      acc[stage.id] = stage.name;
      return acc;
    }, {} as Record<number, string>);

    const result =  [ProjectStatus.ACTIVE, ProjectStatus.CLOSED, ProjectStatus.ARCHIVED].map((item) => {
      const stageId = map[item];
      return {
        status: item,
        stageId,
        stageName: stageId ? stagesMap[stageId] : undefined
      };
    });

    return result;
  }

  return (
    <List
      getDataFunc={loadStages}
      dependencies={[props.projectType]}
      updateOnEvents={[FUB_STATUS_MAP_UPDATED, FUB_PIPELINES_MAPPING_UPDATED]}
    >
      <GridLayout>
        <Cell label="When Transaction in Status">
          <ProjectStatusField />
        </Cell>
        <Cell label="Sync To Fub Stage">
          <TextField source="stageName" noDataMessage="-" />
        </Cell>
        <Cell label="Actions">
          <EditButton projectType={props.projectType} profile={props.profile} />
        </Cell>
      </GridLayout>
    </List>
  );
}

export function StagesToStatusList(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
}) {
  const pipelineMapService = useFubPipelineMappingService();

  const { data, loading } = useDataLoaderNew(
    async () => pipelineMapService.getPipelineMapEntry(props.profile.id, props.projectType),
    {
      events: [FUB_PIPELINES_MAPPING_UPDATED],
      dependencies: [props.profile.id, props.projectType],
    }
  )

  if (!loading) {
    if (data) {
      return (
        <PipelineSetView profile={props.profile} projectType={props.projectType} />
      );
    } else {
      return (<Text>To set up Transaction Status to Follow Up Boss Deal Stage mapping, you first need to link this Transaction Type to a Follow Up Boss pipeline.</Text>);
    }
  } else {
    return (<LoadingAnimation />);
  }
}

export function StagesToStatusListWidget(props: {
  profile: ConnectionProfile,
  projectType: ProjectType,
}) {
  return (
    <AccessChecker permissionToCheck={TeamPermission.MANAGE_FUB_CONNECTION}>
      <Header4>Stages</Header4>
      <StagesToStatusList profile={props.profile} projectType={props.projectType} />
    </AccessChecker>
  );
}
